// Imports ------------------------------------------------------------------->

import {Component, handler, attr} from "../Component";
import {element} from "../CustomElement";
import {i18n} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";

import {Link} from "../flipp-link/Link";
import {LazyImage} from "../flipp-lazy-image/LazyImage";

import {inject} from "../../services/di";
import {ConfigService} from "../../services/ConfigService";
import {UserAgentService} from "../../services/UserAgentService";
import {Flyer} from "../../definitions/FlyerDefinitions";
import {SourceAction} from "../../services/definitions/IAnalyticsService";
import { LocationService } from "../../services/LocationService";
import { isCrawler } from "../../utils/crawler";

// Private Definitions ------------------------------------------------------->

interface ItemLayoutAttributes extends JSX.HTMLProps {
	items: ItemLayoutEntry[];
	/* The algorithm to pre-layout the images assumed that this element node is in the DOM. With
	* the virtual DOM implementation, this may not be the case until AFTER render() occurs and the
	* changes are patched() into the DOM. As a result, we need to detect when this element is not
	* in DOM and use a fallback hint (offsetHint) */
	query?: string;
	offsetHint: number;
	sourceAction: SourceAction;
	merchantLogo?: boolean;
	handleDeleted?: (entry: ItemLayoutEntry) => void;
	handleItemSelected?: (entry: ItemLayoutEntry, index: number) => void;
}

// Public Definitions -------------------------------------------------------->

export interface ItemLayoutEntry {
	flyer?: Flyer;
	merchantLogo?: string;
	validTo?: Date;
	itemId: string;
	imageUrl: string;
	top: number;
	bottom: number;
	left: number;
	right: number;
}

interface PositionedItemEntry {
	item: ItemLayoutEntry;
	flyer: Flyer;
	top: number;
	left: number;
	height: number;
}

@element("flipp-item-layout")
export class ItemLayout extends Component<ItemLayoutAttributes, {}> {
	@attr merchantLogo: boolean;
	@attr offsetHint: number;
	@attr sourceAction: SourceAction;
	@attr query: string;

	@handler items: ItemLayoutEntry[];
	@handler handleDeleted: (entry: ItemLayoutEntry) => void;
	@handler handleItemSelected: (entry: ItemLayoutEntry, index: number) => void;

	@inject config: ConfigService;
	@inject userAgent: UserAgentService;
	@inject location: LocationService;

	onAttach() {
		window.addEventListener("resize", this._handleResize);
	}

	onDetach() {
		window.removeEventListener("resize", this._handleResize);
	}

	renderItemBody(entry: any) {
		return (
			<div>
				<img class="clipping" alt={this.getAlt(entry.item)} src={entry.item.imageUrl}/>
				{entry.item.flyer ? <p>{entry.item.flyer.name}</p> : null}

				{isCrawler() ? <h3>{this.getH3(entry.item)}</h3> : null }
				<div class="rip-cover"></div>
				<img class="rip" src="https://f.wishabi.net/creative/Distribution/images/rip.png"/>
				<img class="rip-hover" src="https://f.wishabi.net/creative/Distribution/images/rip-hover.png"/>
			</div>
		);
	}

	render() {
		let itemBlockWidth = this.offsetWidth;
		if (this.compareDocumentPosition(document.documentElement) & document.DOCUMENT_POSITION_DISCONNECTED) {
			itemBlockWidth = this.offsetHint;
		}

		const layoutItems = this._layoutItems(this.items, itemBlockWidth);
		return <div class="item-block" style={{height: `${layoutItems.containerHeight}px`}}>
			{layoutItems.items.map((entry, idx) => {
				const expired = this.isExpired(entry.item);
				if (isCrawler() && expired) {
					return;
				}
				return (<div item-id={entry.item.itemId}
										class="item-container"
										classList={{
											expired,
											invalid: !entry.item.flyer
										}}
										style={{top: `${entry.top}px`, left: `${entry.left}px`, height: `${entry.height}px`}}>

					{this.handleDeleted && !this.userAgent.mobile ?
						<button class="trash" onClick={this.handleDeleted.bind(this, entry.item)}>
							{icons.garbage}
						</button>
					: null}
					{!isCrawler() ?
						<div class="expired-badge-wrapper">
							<div class="expired-badge">
								{i18n("expired")}
							</div>
						</div>
					: null}
					{entry.item.merchantLogo ?
						<LazyImage
							class="merchant-logo"
							fit
							contain
							alt={entry.item.flyer ? entry.item.flyer.merchant : ""}
							dataSrc={entry.item.merchantLogo}
						/> : null}

					<div class="wrapper">
						{entry.item.flyer ? (
							<Link to={entry.item.flyer}
									params={{
									item_id: entry.item.itemId
									}}
									onClick={this._handleItemClicked.bind(this, entry.item, idx)}
									sourceAction={this.sourceAction}
							>
								{this.renderItemBody(entry)}
							</Link>
						) : this.renderItemBody(entry)}

					</div>
				</div>);
			})}
		</div>;
	}

	private _handleResize = (e: Event) => {
		this.invalidate();
	}

	private _layoutItems = (items: ItemLayoutEntry[], itemBlockWidth: number) => {

		const mql = window.matchMedia("(max-width: 374px)");
		let imageWidth = mql.matches ? 118 : 162;
		let itemWidth = mql.matches ? 128 : 172;
		if (this.merchantLogo) {
			itemWidth = mql.matches ? 150 : 194;
		}
		const numColumns = Math.floor(itemBlockWidth / itemWidth);
		const tops: number[] = [];
		for (let i = 0; i < numColumns; ++i)
			tops.push(0);

		const findSlot = () => {
			let slot = 0;
			for (let i = 0; i < numColumns; ++i)
				if (tops[i] < tops[slot])
					slot = i;

			return slot;
		};

		const result: PositionedItemEntry[] = [];
		items.forEach((entry) => {
			const slot = findSlot();
			const width = entry.right - entry.left;
			const height = entry.top - entry.bottom;
			const aspectRatio = height / width;
			const actualHeight = imageWidth * aspectRatio;
			const imageHeight = actualHeight + 40;

			const left = slot * itemWidth + slot * 10;
			const top = tops[slot];
			tops[slot] += imageHeight;

			result.push({item: entry, flyer: entry.flyer, left, top, height: actualHeight});
		});

		const containerHeight = tops.reduce((lhs, rhs) => {
			return Math.max(lhs, rhs);
		});

		return {
			items: result,
			containerHeight
		};
	}

	private _handleItemClicked = (item: ItemLayoutEntry, idx: number) => {
		if (this.handleItemSelected) {
			this.handleItemSelected(item, idx);
		}
	}

	private isExpired = (item: ItemLayoutEntry) => {
			return item.validTo ? item.validTo < new Date() : false;
	}

	private getAlt = (item: ItemLayoutEntry) => {
		const itemName = item.flyer ? `${item.flyer.merchant} ${item.flyer.name}` : "";
		return i18n("search_page_alt", {
			city: this.location.city,
			itemName
		});
	}

	private getH3 = (item: ItemLayoutEntry) => {
		const itemName = item.flyer ? `${item.flyer.merchant} ${item.flyer.name}` : "";
		const status = this.isExpired(item) ? i18n("expired") : i18n("online");
		return i18n("search_page_h3", {
			itemName,
			status
		});
	}
}
